import React, { useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import { useReactToPrint } from "react-to-print";
import config from "../config/Config";
import { getAppliedJobDetails } from "../helper/My_Helper";

const JoiningChecklist = ({ candidateData = {}}) => {
  const formRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: formRef,
    pageStyle: `
      @page {
        size: A4;
        margin: 10mm 12mm;
      }
      @media print {
        html, body {
          width: 210mm;
          height: 297mm;
          margin: 0;
          padding: 0;
        }
        .no-print {
          display: none !important;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .page-break {
          page-break-inside: avoid;
        }
      }
    `,
  });
  const appliedJob = getAppliedJobDetails(candidateData);
  const getCandidateName = () => {
    const fullName = candidateData?.applicant_form_data?.full_name;
    if (typeof fullName === "object") {
      return [fullName?.first_name, fullName?.middle_name, fullName?.surname]
        .filter(Boolean)
        .join(" ");
    }
    return (
      candidateData?.name ||
      candidateData?.applicant_form_data?.candidate_name ||
      ""
    );
  };

  return (
    <Box>
      {/* Print Button - Hidden on print */}
      <Box sx={{ textAlign: "center", mb: 2 }} className="no-print">
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
      </Box>

      {/* Printable Content */}
      <Box
        ref={formRef}
        sx={{
          backgroundColor: "#fff",
          p: 2.5,
          fontFamily: '"Times New Roman", Times, serif',
          maxWidth: "210mm",
          mx: "auto",
          fontSize: "13px",
          lineHeight: 1.35,
          color: "#000",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "relative",
            mb: 1.5,
            minHeight: "50px",
          }}
        >
          <Box sx={{ position: "absolute", left: 0, top: 0 }}>
            <img
              src={
                config?.IMAGE_PATH
                  ? config.IMAGE_PATH + "logo512.png"
                  : "/logo512.png"
              }
              alt="HLFPPT Logo"
              height="48"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/logo512.png";
              }}
            />
          </Box>

          <Typography
            align="center"
            sx={{
              fontSize: "18px",
              fontWeight: "bold",
              letterSpacing: "0.5px",
              pt: 1,
            }}
          >
            Joining Document Checklist
          </Typography>
        </Box>

        {/* Employee Name (optional helper line) */}
        {getCandidateName() && (
          <Typography sx={{ fontSize: "13px", mb: 1.5 }}>
            <strong>Employee Name:</strong> {getCandidateName()}
          </Typography>
        )}

        {/* Section 1 */}
        <Typography
          sx={{
            fontSize: "13.5px",
            fontWeight: "bold",
            mb: 0.8,
            textDecoration: "underline",
          }}
        >
          Documents to be collected from employee:
        </Typography>

        <Box component="ol" sx={{ pl: 2.5, m: 0, mb: 1.5 }}>
          {[
            "Photocopies and other certificates related to academic/technical/professional qualification duly attested or self attested by employee.",
            "Copies of experience certificates: (if any)",
            "Certificate in support of date of birth, as per certification issued by the education department.",
            "Resignation acceptance and relieving certificate/ letters received from previous employer in hardcopy duly stamped.",
            "Salary certificate or Form 16 of previous employer for the financial year in which employee is joined.",
            "Four passport size photographs with employees signature of the reverse of the photographs.",
            "Residence address proof (Copy of Passport/Ration Card/Driving License/Voter ID Card duly self attested by employee).",
            "Medical Fitness certificate.",
            "Acceptance of offer letter (if not received by HR).",
            "PAN Card",
          ].map((item, index) => (
            <Typography
              component="li"
              key={index}
              sx={{ fontSize: "12.5px", mb: 0.4, pl: 0.5 }}
            >
              {item}
            </Typography>
          ))}
        </Box>

        <Typography
          sx={{
            fontSize: "12px",
            fontStyle: "italic",
            mb: 1.8,
            borderLeft: "3px solid #555",
            pl: 1.2,
          }}
        >
          <strong>Note:</strong> We always send the requirement of above
          documents in the offer letter itself hence employee must carry the
          documents at the time of joining.
        </Typography>

        {/* Section 2 */}
        <Typography
          sx={{
            fontSize: "13.5px",
            fontWeight: "bold",
            mb: 0.8,
            textDecoration: "underline",
          }}
        >
          Joining Forms to be submitted by employee on the joining day itself:
        </Typography>

        <Box component="ol" sx={{ pl: 2.5, m: 0, mb: 1 }}>
          {[
            "Application Form-HLFPPT (if not submitted at the time of interview)",
            "Joining Report Form",
            "PF nomination form (In form - 2)",
            "Nomination form for group Personal Accident Policy.",
            "Declaration Form for Group Mediclaim Policy.",
            "Nomination form - Gratuity (in form F)",
            "UAN Form, (PF Declaration Form 11)",
            "Investment declaration form 12BB",
            "Form 12 B for current Financial in which employee joins (should be supported by Form -16 / Salary Certificate of previous employer)",
          ].map((item, index) => (
            <Typography
              component="li"
              key={index}
              sx={{ fontSize: "12.5px", mb: 0.4, pl: 0.5 }}
            >
              {item}
            </Typography>
          ))}
        </Box>

        <Typography
          sx={{
            fontSize: "12px",
            fontStyle: "italic",
            mb: 2,
            borderLeft: "3px solid #555",
            pl: 1.2,
          }}
        >
          <strong>Note:</strong> Serial no. 8-9 is required to be filled by
          employee, only if his/her salary is tax liable (annual gross income
          beyond Rs. 2,50,000/-)
        </Typography>

        {/* Remarks */}
        <Typography
          sx={{ fontSize: "13px", fontWeight: "bold", mb: 0.8 }}
        >
          Remarks (if any):
        </Typography>

        <Box sx={{ mb: 2.5 }}>
          {[1, 2, 3, 4].map((num) => (
            <Box
              key={num}
              sx={{
                display: "flex",
                alignItems: "flex-end",
                mb: 1.1,
              }}
            >
              <Typography sx={{ fontSize: "12.5px", minWidth: "18px" }}>
                {num}.
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  borderBottom: "1px solid #000",
                  height: "18px",
                  ml: 0.5,
                }}
              />
            </Box>
          ))}
        </Box>

        {/* Verification Footer */}
        <Box className="page-break" sx={{ mt: 1 }}>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: "bold",
              mb: 1.5,
              textDecoration: "underline",
            }}
          >
            Documents checked and verified by
          </Typography>

          <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography sx={{ fontSize: "12.5px", mb: 0.3 }}>
              Signature:
            </Typography>

            {appliedJob?.check_list?.verifiedBy?.signature ? (
              <Box
                sx={{
                  height: "28px",
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img
                  src={
                    config?.IMAGE_PATH
                      ? config.IMAGE_PATH +
                      appliedJob.check_list.verifiedBy.signature
                      : appliedJob.check_list.verifiedBy.signature
                  }
                  alt="HR Representative Signature"
                  style={{
                    maxHeight: "28px",
                    maxWidth: "150px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  borderBottom: "1px solid #000",
                  height: "28px",
                  mb: 1.5,
                }}
              />
            )}
          </Grid>

          <Grid item xs={6}>
            <Typography sx={{ fontSize: "12.5px", mb: 0.3 }}>
              Name of HR representative:
            </Typography>

            <Box
              sx={{
                borderBottom: "1px solid #000",
                minHeight: "28px",
                mb: 1.5,
              }}
            >
              <Typography sx={{ fontSize: "12.5px" }}>
                {appliedJob?.check_list?.verifiedBy?.name || ""}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Typography sx={{ fontSize: "12.5px", mb: 0.3 }}>
              Designation:
            </Typography>

            <Box
              sx={{
                borderBottom: "1px solid #000",
                minHeight: "28px",
              }}
            >
              <Typography sx={{ fontSize: "12.5px" }}>
                {appliedJob?.check_list?.verifiedBy?.designation || ""}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Typography sx={{ fontSize: "12.5px", mb: 0.3 }}>
              Date:
            </Typography>

            <Box
              sx={{
                borderBottom: "1px solid #000",
                minHeight: "28px",
              }}
            >
              <Typography sx={{ fontSize: "12.5px" }}>
                {appliedJob?.check_list?.verifiedAt
                  ? new Date(
                    appliedJob.check_list.verifiedAt
                    ).toLocaleDateString("en-IN")
                  : ""}
              </Typography>
            </Box>
          </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default JoiningChecklist;